/**
 * The quote line editor's block, built from the quote and its stored lines.
 *
 * Shared by the server (the conversational draft returns one) and the client
 * (it rebuilds one from `GET /api/quotes/[id]` after each step), so both read
 * the same rows the same way. Pure and directive-free (anti-pattern #18).
 */
import type { QuoteLineInput } from '@/lib/quotes/line-input';
import type { LineEditorBlock } from './ui-blocks';

/** The quote columns the block reads. `quotes.*` and the draft RPC both carry them. */
export interface QuoteRowForEditor {
  id: string;
  reference_number?: string | null;
  status?: string | null;
  subtotal?: number | string | null;
  tax_rate?: number | string | null;
  tax_amount?: number | string | null;
  total?: number | string | null;
}

/**
 * A `quote_line_items` row: every column the quote save writes back.
 *
 * 🚨 `PATCH /api/quotes/[id]` deletes every line and re-inserts the array it is
 * sent, so a column missing here is not left alone, it is RESET (CLAUDE.md #28,
 * the `line_type` incident). `quoteLineInputFromRow` must carry all of them;
 * `line-editor-block.test.ts` fails when the route writes a column it does not.
 */
export interface QuoteLineRow {
  id: string;
  description: string | null;
  detail?: string | null;
  quantity: number | string;
  unit_price: number | string;
  sort_order?: number | null;
  price_list_item_id?: string | null;
  selection_kind?: string | null;
  option_group?: string | null;
  is_default?: boolean | null;
  image_path?: string | null;
}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (n: number) => Math.round(n * 100) / 100;

/**
 * The block, or null when the quote is not there to show. Lines come in
 * `sort_order`; an option or an extra is shown but not steppable, because
 * changing one changes which configuration the total describes.
 */
export function lineEditorBlock(quote: QuoteRowForEditor | null | undefined, lines: readonly QuoteLineRow[] | null | undefined): LineEditorBlock | null {
  if (!quote?.id || !Array.isArray(lines)) return null;
  const ordered = [...lines].sort((a, b) => num(a.sort_order) - num(b.sort_order));
  return {
    kind: 'line-editor',
    quoteId: quote.id,
    reference: quote.reference_number || 'Draft quote',
    status: quote.status || 'draft',
    lines: ordered.map(l => ({
      id: l.id,
      description: (l.description ?? '').trim() || 'Line',
      quantity: num(l.quantity),
      unitPrice: num(l.unit_price),
      lineTotal: money(num(l.quantity) * num(l.unit_price)),
      steppable: !l.selection_kind || l.selection_kind === 'fixed',
    })),
    subtotal: money(num(quote.subtotal)),
    taxRate: num(quote.tax_rate),
    taxAmount: money(num(quote.tax_amount)),
    total: money(num(quote.total)),
  };
}

/** A stored line, as the quote save expects to receive it — every column carried. */
export function quoteLineInputFromRow(row: QuoteLineRow): QuoteLineInput {
  return {
    description: row.description ?? '',
    detail: row.detail ?? null,
    quantity: num(row.quantity),
    unitPrice: num(row.unit_price),
    price_list_item_id: row.price_list_item_id ?? null,
    kind: row.selection_kind ?? 'fixed',
    group: row.option_group ?? null,
    isDefault: row.is_default === true,
    image_path: row.image_path ?? null,
  };
}

/** The smallest quantity a step may leave. Removing a line is a typed edit, not a tap. */
export function minimumStepQuantity(quantity: number): number {
  return quantity >= 1 ? 1 : quantity;
}
